/**
 * Browser-side audio decode + downmix + resample to 16 kHz mono.
 *
 * Uses Web Audio: `decodeAudioData` inflates the whole file to PCM at the
 * source rate, then an `OfflineAudioContext` renders it down to 16 kHz
 * mono. Peak memory is proportional to duration × source sample rate ×
 * channels — the 2-hour v1 cap exists because of exactly this (a streaming
 * ffmpeg.wasm path is the planned v2 fix for very long recordings).
 */

export const TARGET_SAMPLE_RATE = 16_000;

export interface DecodedAudio {
  samples: Float32Array<ArrayBuffer>;
  sampleRate: typeof TARGET_SAMPLE_RATE;
  durationSec: number;
}

export class AudioDecodeError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "AudioDecodeError";
  }
}

export async function decodeToMono16k(file: File): Promise<DecodedAudio> {
  const bytes = await file.arrayBuffer();

  const AudioContextCtor = window.AudioContext;
  if (!AudioContextCtor || typeof OfflineAudioContext === "undefined") {
    throw new AudioDecodeError("This browser does not support Web Audio decoding.");
  }

  const decodeContext = new AudioContextCtor();
  let decoded: AudioBuffer;
  try {
    decoded = await decodeContext.decodeAudioData(bytes);
  } catch (error) {
    throw new AudioDecodeError(
      "Could not decode this file as audio. Try an mp3, m4a, wav, or webm file.",
      { cause: error },
    );
  } finally {
    // decodeAudioData holds no per-file state we need afterwards.
    void decodeContext.close().catch(() => {});
  }

  const targetLength = Math.ceil(decoded.duration * TARGET_SAMPLE_RATE);
  const offline = new OfflineAudioContext(1, targetLength, TARGET_SAMPLE_RATE);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();

  const rendered = await offline.startRendering();
  return {
    samples: rendered.getChannelData(0),
    sampleRate: TARGET_SAMPLE_RATE,
    durationSec: rendered.duration,
  };
}
